import { Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { ActionButton, Card, FramedIcon } from '~components';
import LastOnline from '~components/data/lastOnline';
import RowActionsCell from '~components/dataGrids/rowActionsCell';
import RasterImage from '~components/media/rasterImage';
import VectorImage from '~components/media/vectorImage';
import { StatusVariant } from '~enums';
import DevicesService from '~features/devices/services/devicesService';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

interface ControlBoxCardProps {
	deviceId?: string | undefined;
	organisationId: string | undefined;
	onLink: () => void;
	onViewDetails: () => void;
	onUnlink: () => void;
	isLoading?: boolean;
}

const deviceService = getService(DevicesService);

const ControlBoxCard = ({
	deviceId,
	organisationId,
	onLink,
	onViewDetails,
	onUnlink,
	isLoading = false,
}: ControlBoxCardProps) => {
	const { t } = useTranslation();
	// Load device
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(deviceService.getDeviceByIdCacheKey(deviceId, organisationId), ([_, id, orgId]) => {
		if (id == undefined) return;
		return deviceService.getDeviceById(id, orgId ?? undefined);
	});

	const resolvedLoading = isLoading || deviceIsLoading;
	return (
		<Card density='dense'>
			<Stack
				direction='row'
				spacing={Dimens.spacingXl}
				alignItems='center'
				sx={{
					paddingX: Dimens.spacingMd,
				}}
			>
				<Box
					sx={{
						height: '56px',
						width: '56px',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					{resolvedLoading ?
						<Skeleton sx={{ height: '100%', width: '100%', transform: 'none' }} />
					: deviceData ?
						<RasterImage name='device/model/skbr-ctrl' size='100%' />
					:	<FramedIcon name='icon/link' size='small' />}
				</Box>
				<Stack sx={{ flexGrow: 1 }}>
					<Typography variant='label'>
						{resolvedLoading ?
							<Skeleton sx={{ width: '240px' }} />
						: deviceData ?
							`${deviceData.name} (${deviceData.hardwareId})`
						:	t('device.labels.linkAControlBox', { ns: 'entities' })}
					</Typography>
					{resolvedLoading ?
						<Skeleton sx={{ width: '200px' }} />
					: deviceData ?
						<LastOnline date={deviceData?.state?.lastReport} />
					:	<Typography variant='paragraph'>
							{t('device.labels.linkAControlBoxDescription', { ns: 'entities' })}
						</Typography>
					}
				</Stack>
				{!resolvedLoading &&
					(deviceData ?
						<RowActionsCell
							actions={[
								{
									key: 'view-details',
									label: t('viewDetails', { ns: 'actions' }),
									icon: <VectorImage name='icon/eye' />,
									onClick: onViewDetails,
								},
								{
									key: 'unlink',
									label: t('unlink', { ns: 'actions' }),
									icon: <VectorImage name='icon/unlink' />,
									variant: StatusVariant.Destructive,
									onClick: onUnlink,
								},
							]}
						/>
					:	<ActionButton
							label={t('device.labels.linkAControlBox', { ns: 'entities' })}
							onClick={onLink}
						/>)}
			</Stack>
		</Card>
	);
};

export default ControlBoxCard;
